'use client';
import { Box, Button, Typography } from '@mui/material';

interface SideSelectorProps {
  selectedSide: 'yes' | 'no';
  outcomeNameYes: string;
  outcomeNameNo: string;
  yesPrice: number;
  noPrice: number;
  yesBtnColor?: string;
  noBtnColor?: string;
  onYesClick: () => void;
  onNoClick: () => void;
  isFlipped?: boolean;
}

export function SideSelector({
  selectedSide,
  outcomeNameYes,
  outcomeNameNo,
  yesPrice,
  noPrice,
  yesBtnColor = '#22c55e',
  noBtnColor = '#ef4444',
  onYesClick,
  onNoClick,
  isFlipped = false,
}: SideSelectorProps) {
  // Define data for the two slots (Top and Bottom) based on the flip state.
  // This ensures that the component logic (names, prices, colors, and clicks)
  // always aligns with the visual ordering.
  const topData = {
    name: isFlipped ? outcomeNameNo : outcomeNameYes,
    price: isFlipped ? noPrice : yesPrice,
    color: isFlipped ? noBtnColor : yesBtnColor,
    side: isFlipped ? ('no' as const) : ('yes' as const),
    onClick: isFlipped ? onNoClick : onYesClick,
  };

  const bottomData = {
    name: isFlipped ? outcomeNameYes : outcomeNameNo,
    price: isFlipped ? yesPrice : noPrice,
    color: isFlipped ? yesBtnColor : noBtnColor,
    side: isFlipped ? ('yes' as const) : ('no' as const),
    onClick: isFlipped ? onYesClick : onNoClick,
  };

  const renderSideButton = (data: typeof topData) => {
    const isActive = selectedSide === data.side;
    return (
      <Button
        fullWidth
        variant="contained"
        onClick={data.onClick}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          bgcolor: isActive ? data.color : '#2d2e32',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '1rem',
          py: 1.5,
          px: 2,
          borderRadius: '8px',
          textTransform: 'none',
          border: '1px solid',
          borderColor: isActive ? data.color : 'transparent',
          boxShadow: (theme) => {
            const color = isActive ? data.color : '#2d2e32';
            const depth = '4px';
            return isActive
              ? `0 ${depth} 0 color-mix(in srgb, ${color} 60%, black), 0 1px 4px rgba(0,0,0,0.4)`
              : `0 ${depth} 0 rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)`;
          },
          transform: 'translateY(0px)',
          transition: 'all 0.12s ease',
          '&:hover': {
            bgcolor: isActive ? data.color : '#3a3b3f',
            transform: 'translateY(1px)',
            opacity: 0.95,
          },
          '&:active': {
            transform: 'translateY(2px)',
            boxShadow: 'none',
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {data.name}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
          {(data.price * 100).toFixed(0)}¢
        </Typography>
      </Button>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, mb: 3 }}>
      {renderSideButton(topData)}
      {renderSideButton(bottomData)}
    </Box>
  );
}
